import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '../index';
import { BudtenderUserWrite, UserLogin } from '../../common/types';
import JwtService, { ID_ACCESS_TOKEN_KEY } from '../../common/jwt.service';
import router from '../../router';
import {
  BUDTENDER_PORTAL,
  HOME_PAGE,
  LOGIN_PAGE,
} from '../../common/routes.config';
import Budtender from '../../common/model/Budtender';
import BudtenderService from '../../common/api/budtender.service';
import Dispensary from '../../common/model/Dispensary';
import Brand from '../../common/model/Brand';
import brandModule from './brand.module';

const jwtService = new JwtService();

@Module({
  dynamic: true,
  store,
  name: 'budtender',
})
class BudtenderModule extends VuexModule {
  budtender: Budtender | null = null;

  token: string | null = jwtService.getToken(ID_ACCESS_TOKEN_KEY);

  service = new BudtenderService();

  dispensary: Dispensary | null = null;

  dispensaryEmployees: Array<Budtender> = [];

  brands: Array<Brand> = [];

  selectedBrand: Brand | null = null;

  eventSignature = '';

  // Set brand when budtender visits brand's site, or when admin enters portal.
  @Mutation
  SET_BUDTENDER(budtender) {
    this.budtender = budtender;
  }

  @Mutation
  SET_SELECTED_BRAND(selectedBrand) {
    this.selectedBrand = selectedBrand;
  }

  @Mutation
  SET_DISPENSARY(dispensary) {
    this.dispensary = dispensary;
  }

  @Mutation
  SET_DISPENSARY_EMPLOYEES(employees) {
    this.dispensaryEmployees = employees;
  }

  @Mutation
  SET_BRANDS(brands) {
    this.brands = brands;
  }

  @Mutation
  PURGE_AUTH() {
    this.budtender = null;
    this.token = null;
  }

  @Mutation
  SET_EVENT_SIGNATURE(eventSignature) {
    this.eventSignature = eventSignature;
  }

  @Mutation
  SET_JWT(token) {
    // Save to Authorization header
    this.service.setHeader(token);
    // Store in localstorage
    jwtService.saveToken(ID_ACCESS_TOKEN_KEY, token);
    this.token = token;
  }

  @Action
  async createBudtender(budtender: BudtenderUserWrite): Promise<void> {
    const response = await this.service.createBudtender(budtender);
    if (!response) {
      return;
    }
    this.SET_JWT(response.data.data.token);
    this.SET_BUDTENDER(response.data.data.user);
    this.SET_DISPENSARY(response.data.data.user.dispensary);
    this.SET_DISPENSARY_EMPLOYEES(response.data.data.user.dispensary.budtenders)
    this.SET_BRANDS(response.data.data.user.dispensary.brands);
    // User has been created and can proceed into the budtender portal.
    router.push(BUDTENDER_PORTAL);
  }

  @Action({ rawError: true })
  async budtenderLogin(payload: UserLogin): Promise<void> {
    const response = await this.service.login(payload);
    if (!response) {
      return;
    }
    this.SET_JWT(response.data.data.token);
    this.SET_BUDTENDER(response.data.data.user);
    this.SET_DISPENSARY(response.data.data.user.dispensary);
    this.SET_DISPENSARY_EMPLOYEES(response.data.data.user.dispensary.budtenders)
    // User has been authenticated and can proceed into the budtender portal.
    router.push(BUDTENDER_PORTAL);
  }

  @Action({ rawError: true })
  async fetchBudtender(): Promise<void> {
    if (!this.token) { return; }
    this.service.setHeader(this.token);
    const response = await this.service.fetchBudtender();
    if (!response) {
      await jwtService.destroyToken(ID_ACCESS_TOKEN_KEY);
      this.PURGE_AUTH();
      router.push(LOGIN_PAGE);
      return;
    }
    this.SET_BUDTENDER(response.data.data.user);
    this.SET_DISPENSARY(response.data.data.user.dispensary);
    this.SET_DISPENSARY_EMPLOYEES(response.data.data.user.dispensary.budtenders)
    this.SET_BRANDS(response.data.data.user.dispensary.brands);
  }

  @Action
  async selectBrand(brand: Brand): Promise<void> {
    this.SET_SELECTED_BRAND(brand);
    brandModule.SET_BRAND(brand);
    router.push(HOME_PAGE);
  }

  @Action
  async budtenderLogout(): Promise<void> {
    await jwtService.destroyToken(ID_ACCESS_TOKEN_KEY);
    this.PURGE_AUTH();
    router.push(LOGIN_PAGE);
  }

  @Action
  async activateBudtender(payload): Promise<void> {
    const response = await this.service.activateBudtender(payload);
    if (!response) {
      return;
    }
    this.SET_JWT(response.data.data.token);
    this.SET_BUDTENDER(response.data.data.user);
    this.SET_DISPENSARY(response.data.data.user.dispensary);
    this.SET_BRANDS(response.data.data.user.dispensary.brands);
    // User has been created and can proceed into the budtender portal.
    router.push(BUDTENDER_PORTAL);
  }

  @Action
  async inviteBudtender(payload): Promise<void> {
    await this.service.inviteBudtender(payload);
  }

  @Action
  async addBudtenderToMeeting(payload): Promise<void> {
    const response = await this.service.addBudtenderToMeeting(payload);
    if (!response) { return; }
    const { signature } = response.data.data;
    this.SET_EVENT_SIGNATURE(signature);
  }

  @Action
  async initiateBudtenderPasswordReset(payload): Promise<void> {
    await this.service.initiatePasswordReset(payload);
  }

  @Action
  async confirmBudtenderPasswordReset(payload): Promise<void> {
    await this.service.confirmPasswordReset(payload);
  }
}

export default getModule(BudtenderModule);
